import { useEffect, useState } from 'react';
import { Button, Card, CardHeader, Checkbox, Divider, Grid, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { makeStyles } from "@mui/styles";

import { useSelector } from 'react-redux';
import { selectLists } from '../reducers/listSlice';
import { FixedSizeList } from 'react-window';

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
};

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
};

function union(a, b) {
  return [...a, ...not(b, a)];
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    marginTop: 10,
    width: '100%',
    position: 'relative'
  },
  listHeader: {
    position: 'fixed'
  },
  listContainer: {
    width: '45%',
  },
  list: {
    maxHeight: 300,
    minHeight: 300,
    overflow: 'auto',
  }
}));

export const EconomicsListsTransferList = ({ allLists, listIds, setListIds }) => {
  const classes = useStyles();
  const lists = useSelector(selectLists);

  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(() => not(allLists, listIds));

  const leftChecked = intersection(checked, left);
  const listsChecked = intersection(checked, listIds);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    };

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    };
  };

  const handleCheckedRight = () => {
    setListIds(() => listIds.concat(leftChecked));
    setLeft(() => not(left, leftChecked));
    setChecked(() => not(checked, leftChecked));
  };

  const handleAllRight = () => {
    setListIds(() => listIds.concat(left));
    setLeft([]);
  };

  const handleCheckedLeft = () => {
    setListIds(() => left.concat(listsChecked));
    setLeft(() => not(left, listsChecked));
    setChecked(() => not(checked, listsChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(listIds));
    setListIds([]);
  };

  useEffect(() => {
    setLeft(allLists);
  }, [allLists]);


  function renderLeftSourceRow(props) {
    const { index, style } = props;
    return (
      <ListItem
        key={left[index]}
        style={{...style, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center'}}
        role="listitem"
        button
        onClick={handleToggle(left[index])}
        dense
      >
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(left[index]) !== -1}
            tabIndex={-1}
            disableRipple
            color="primary"
          />
        </ListItemIcon>
        <ListItemText 
          primary={<Typography noWrap variant="body2">{lists.byId[left[index]]?.name}</Typography>} 
          secondary={<Typography variant="caption" color="textSecondary" noWrap>Wells: {lists.byId[left[index]]?.wellUwis?.length || 0}</Typography>} 
        />
      </ListItem>
    );
  };

  function renderRightSourceRow(props) {
    const { index, style } = props;
    return (
      <ListItem
        key={listIds[index]}
        style={{...style, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center'}}
        role="listitem"
        button
        onClick={handleToggle(listIds[index])}
        dense
      >
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(listIds[index]) !== -1}
            tabIndex={-1}
            disableRipple
            color="primary"
          />
        </ListItemIcon>
        <ListItemText 
          primary={<Typography noWrap variant="body2">{lists?.byId[listIds[index]]?.name}</Typography>} 
          secondary={<Typography variant="caption" color="textSecondary" noWrap>Wells: {lists?.byId[listIds[index]]?.wellUwis?.length || 0}</Typography>} 
        />
      </ListItem>
    );
  };

  const customList = (title, items, side) => (
    <Card style={{padding: 0, margin: 0, width: '100%'}}>
      <CardHeader
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={
              numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            color="primary"
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
        style={{paddingBottom: 10}}
      />
      <Divider />
      <FixedSizeList
        height={300}
        width={'100%'}
        itemSize={54}
        itemCount={side === 'left' ? left?.length : listIds?.length}
      >
        {side === 'left' ? renderLeftSourceRow : renderRightSourceRow}
      </FixedSizeList>
    </Card>
  );

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center" className={classes.root}>
      <Grid item className={classes.listContainer}>{customList('All Lists', left, 'left')}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={left.length === 0}
          >
            ≫
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={listsChecked.length === 0}
          >
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={listIds.length === 0}
          >
            ≪
          </Button>
        </Grid>
      </Grid>
      <Grid item className={classes.listContainer}>{customList('Economics Forecast', listIds, 'right')}</Grid>
    </Grid>
  );
};
